import { LOCAL_ROLES, MANAGED_ROLES, type LocalRole, type ManagedRole } from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchUserRolesJournal, setUserRoles } from '../api';

const MANAGED: ReadonlyArray<string> = MANAGED_ROLES;

/** What the superadmin is editing on the «Ролі» card — the managed part only. */
export interface RolesDraft {
  admin: boolean;
  superadmin: boolean;
  local: LocalRole | null;
}

/**
 * `users.role` as a clean array — the same normalisation the API applies: a
 * legacy single string or a comma-joined element (`"admin, local_owner"`) is
 * split, trimmed, emptied entries dropped and duplicates removed.
 */
export function normaliseRoles(raw: unknown): string[] {
  const parts: unknown[] = Array.isArray(raw) ? raw : [raw];
  const out: string[] = [];
  for (const part of parts) {
    if (typeof part !== 'string') continue;
    for (const piece of part.split(',')) {
      const role = piece.trim();
      if (role && !out.includes(role)) out.push(role);
    }
  }
  return out;
}

/** Roles the card does not manage (`user`, `dealer`, …) — shown read-only. */
export function unmanagedRoles(roles: string[]): string[] {
  return roles.filter((role) => !MANAGED.includes(role));
}

function isLocalRole(role: string): role is LocalRole {
  return (LOCAL_ROLES as ReadonlyArray<string>).includes(role);
}

/** The stored roles as a draft. */
export function draftOf(roles: string[]): RolesDraft {
  return {
    admin: roles.includes('admin'),
    superadmin: roles.includes('superadmin'),
    local: roles.find(isLocalRole) ?? null,
  };
}

/** The PUT body's `roles`, in canonical order (`UserRolesPutSchema`). */
export function managedRolesOf(draft: RolesDraft): ManagedRole[] {
  const roles: ManagedRole[] = [];
  if (draft.admin) roles.push('admin');
  if (draft.superadmin) roles.push('superadmin');
  if (draft.local) roles.push(draft.local);
  return roles;
}

export function isRolesDraftDirty(saved: RolesDraft, draft: RolesDraft): boolean {
  return (
    saved.admin !== draft.admin || saved.superadmin !== draft.superadmin || saved.local !== draft.local
  );
}

/** Ticking «Суперадмін» ticks «Адміністратор» (superadmin ⇒ admin). */
export function withSuperadmin(draft: RolesDraft, on: boolean): RolesDraft {
  return on ? { ...draft, superadmin: true, admin: true } : { ...draft, superadmin: false };
}

/** Unticking «Адміністратор» unticks «Суперадмін» (superadmin ⇒ admin). */
export function withAdmin(draft: RolesDraft, on: boolean): RolesDraft {
  return on ? { ...draft, admin: true } : { ...draft, admin: false, superadmin: false };
}

export const userRolesJournalKey = (userId: string) => ['user', userId, 'roles', 'journal'] as const;

/** Journal read and the Save mutation of the «Ролі» card. */
export function useUserRoles(userId: string) {
  const queryClient = useQueryClient();

  const journalQuery = useQuery({
    queryKey: userRolesJournalKey(userId),
    queryFn: () => fetchUserRolesJournal(userId),
  });

  const saveMutation = useMutation({
    mutationFn: (draft: RolesDraft) => setUserRoles(userId, { roles: managedRolesOf(draft) }),
    onSuccess: async () => {
      // `['user', id]` is the client page's own query — the header badge and
      // this card's saved state both read it; the journal sits under it too.
      await queryClient.invalidateQueries({ queryKey: ['user', userId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  return { journalQuery, saveMutation };
}
